/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useEffect, useState } from "react";
import {
  FlowTypes,
  parseDeviceMakeFromDeviceInfo,
  parseDeviceMakeFromOemInfo,
  useQueryParams,
  useSessionStorage,
} from "@trailhead/smart-setup-core";
import { useDeviceState } from "@trailhead/global-device-context";
import { getUserBrowserId, updateStickyProps } from "@trailhead/analytics";
import {
  getAuthenticationState,
  getUser,
} from "@soluto-private/mx-app-authentication";
import { linkGeneratorGateway } from "@trailhead/link-generator-api";
import { useMxContext } from "@trailhead/mx-context";
import { Partners } from "@trailhead/smart-setup-core";

export const getFullstorySessionUrl = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const fsNamespace = window?.FS;
  if (fsNamespace) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    const getCurrentSessionUrl = fsNamespace.getCurrentSessionURL;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-unsafe-call
    return getCurrentSessionUrl ? getCurrentSessionUrl() : null;
  }
  return null;
};

export const useInitialize = (): boolean => {
  const [initializing, setInitializing] = useState(true);
  const [flowType, setFlowType] = useSessionStorage("flowType", "");

  const params = useQueryParams();
  const {
    updateNewDeviceObName,
    updateOldDeviceObName,
    updateOldDeviceFunctional,
    updateOldDeviceAccess,
    updateNewDeviceActivated,
    updateDeviceFlowType,
    newDeviceObName,
    oldDeviceObName,
  } = useDeviceState();

  const { client } = useMxContext();

  useEffect(() => {
    const parseParams = async () => {
      const campaignId = params.get("cid") ?? "null";
      const newDevice = params.get("newDevice") ?? newDeviceObName;
      const oldDevice = params.get("oldDevice") ?? oldDeviceObName;
      let oldDeviceOs = params.get("oldDeviceOs") ?? "null";
      let oldDeviceOem = params.get("oldDeviceOem") ?? "null";
      let newDeviceOem = params.get("newDeviceOem") ?? "null";
      let newDeviceOs = params.get("newDeviceOs") ?? "null";
      let encryptedMdn = params.get("encryptedMdn") ?? "null";
      const userId = params.get("userId");
      const skipPathfinder = params.get("skipPathfinder") ?? false;
      const browserId = params.get("userBrowserId") ?? getUserBrowserId();
      const linkGeneratorUUID = params.get("uuid") ?? false;
      const oldDeviceFunctional = params.get("oldDeviceFunctional");
      const oldDeviceAccess = params.get("oldDeviceAccess");
      const newDeviceActivated = params.get("newDeviceActivated");
      let flow = flowType;

      if (oldDeviceFunctional) {
        updateOldDeviceFunctional(oldDeviceFunctional === "true");
      }

      if (oldDeviceAccess) {
        updateOldDeviceAccess(oldDeviceAccess === "true");
      }

      if (newDeviceActivated) {
        updateNewDeviceActivated(newDeviceActivated === "true");
      }

      const user = await getUser();

      if (!flowType || params) {
        const flowParam = params.get("flowType");
        if (flowParam !== null) {
          flow =
            flowParam === FlowTypes.NewDevice
              ? FlowTypes.NewDevice
              : FlowTypes.Replacement;
          setFlowType(flow);
          updateDeviceFlowType(flow as FlowTypes);
        }
      }

      if (linkGeneratorUUID) {
        const linkData = await linkGeneratorGateway.getLinkData(
          client as Partners,
          linkGeneratorUUID,
        );
        if (typeof linkData !== "undefined") {
          // TODO: create mapper function based on partner/client
          encryptedMdn = linkData.customerPhoneNumber;

          if (linkData.oldDevice) {
            oldDeviceOem = linkData.oldDevice.oem;
            oldDeviceOs = linkData.oldDevice.os;
          }
          if (linkData.newDevice) {
            newDeviceOem = linkData.newDevice.oem;
            newDeviceOs = linkData.newDevice.os;
          }
        }
      }

      if (newDevice && oldDevice) {
        const newDeviceName = parseDeviceMakeFromDeviceInfo(newDevice);
        const oldDeviceName = parseDeviceMakeFromDeviceInfo(oldDevice);

        updateNewDeviceObName(newDeviceName);
        updateOldDeviceObName(oldDeviceName);
      } else if (newDeviceOem && oldDeviceOem) {
        const newDeviceName = parseDeviceMakeFromOemInfo(
          newDeviceOem,
          newDeviceOs,
        );
        const oldDeviceName = parseDeviceMakeFromOemInfo(
          oldDeviceOem,
          oldDeviceOs,
        );

        if (newDeviceName) {
          updateNewDeviceObName(newDeviceName);
        }
        if (oldDeviceName) {
          updateOldDeviceObName(oldDeviceName);
        }
      }

      if (campaignId) {
        updateStickyProps({ cid: campaignId });
      }

      updateStickyProps({
        userBrowserId: browserId,
        oldDeviceObName,
        flowType: flow,
        newDeviceObName,
        oldDeviceOs,
        oldDeviceOem,
        newDeviceOs,
        newDeviceOem,
        encryptedMdn,
        fullstorySessionUrl: getFullstorySessionUrl(),
        userId,
        storeId: params.get("storeId"),
        repId: params.get("repId"),
        correlationId: params.get("correlationId"),
        appName: "device-setup",
        authStatus: getAuthenticationState(),
        asurionId: user?.profile.asurion_id,
        pathfinderFlow: skipPathfinder,
      });

      setInitializing(false);
    };

    parseParams();
  }, []);

  return initializing;
};
